// src/pages/Ventures.js — Rainmaker Ventures (migrations/090): the weekly
// doc's B-series (B-APEX, B-FLLM...). Rainmaker work, kept apart from RGA's
// mandates. Each one has its own page (VentureDetail.js) for its people and
// firms.
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { EventModal } from './Events'

const EMPTY = { ref: '', name: '', description: '', owner_user_id: '', notes: '' }

// RGA staff only (no partner logins), active.
export function useUsers() {
  const { data } = useQuery({
    queryKey: ['users', 'staff'],
    queryFn: () => api.get('/api/users', { params: { staff: 'true' } }).then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  })
  return data ?? []
}

// Create and edit.
export function VentureForm({ initial, onSubmit, onCancel, submitting, error }) {
  const [form, setForm] = useState(() => {
    const f = { ...EMPTY }
    for (const k of Object.keys(EMPTY)) if (initial?.[k] != null) f[k] = initial[k]
    return f
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const users = useUsers()

  function submit(e) {
    e.preventDefault()
    const payload = {}
    for (const k of Object.keys(EMPTY)) payload[k] = form[k].trim() ? form[k].trim() : null
    onSubmit(payload)
  }

  return (
    <form onSubmit={submit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
        <div>
          <label className="input-label">Ref *</label>
          <input className="input" value={form.ref} onChange={e => set('ref', e.target.value)} required autoFocus placeholder="B-FLLM" />
        </div>
        <div>
          <label className="input-label">Name *</label>
          <input className="input" value={form.name} onChange={e => set('name', e.target.value)} required />
        </div>
      </div>
      <div>
        <label className="input-label">What it is</label>
        <textarea className="textarea" rows={2} value={form.description} onChange={e => set('description', e.target.value)} />
      </div>
      <div style={{ maxWidth: 240 }}>
        <label className="input-label">Owner</label>
        <select className="select" value={form.owner_user_id} onChange={e => set('owner_user_id', e.target.value)}>
          <option value="">None</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div>
        <label className="input-label">Notes</label>
        <textarea className="textarea" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Add the people and firms on the venture's page once it's saved.
        </div>
      </div>
      {error && <div style={{ fontSize: 13, color: 'var(--danger-text)' }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={submitting || !form.ref.trim() || !form.name.trim()}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

// Face tile: the Dashboard's MandateTile frame (size, outline, type scale)
// with the name, the B- ref in the logo slot, and a short narrative
// description. Status, next step, and people and firms live on the
// venture's page.
function VentureTile({ venture: v }) {
  const navigate = useNavigate()
  const go = () => navigate(`/ventures/${v.venture_id}`)

  return (
    <div
      className="card-hover"
      onClick={go}
      role="link"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 160,
        background: 'var(--surface)',
        border: '1px solid var(--tile-outline)',   // cream, as on the mandate tiles
        borderRadius: '8px',
        padding: '16px 20px 24px',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div style={{ minWidth: 0, fontWeight: 600, fontSize: 19, color: 'var(--text-primary)' }}>
          {v.name}
        </div>
        <div style={{
          flexShrink: 0,
          padding: '6px 10px',
          border: '1px solid var(--tile-outline)',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: 'var(--text-secondary)',
          whiteSpace: 'nowrap',
        }}>
          {v.ref}
        </div>
      </div>
      {v.description && (
        <div style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          {v.description}
        </div>
      )}
    </div>
  )
}

export default function Ventures() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['ventures'],
    queryFn: () => api.get('/api/ventures').then(r => r.data.data ?? []),
  })

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/ventures', payload).then(r => r.data.data),
    onSuccess: v => {
      qc.invalidateQueries({ queryKey: ['ventures'] })
      navigate(`/ventures/${v.venture_id}`)
    },
  })

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (data ?? []).filter(v => !q ||
      [v.ref, v.name, v.description, v.owner?.name].some(x => (x || '').toLowerCase().includes(q)))
  }, [data, search])

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-text">
          <h1>Rainmaker Ventures</h1>
        </div>
        <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>+ New Venture</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 280 }} placeholder="Search by ref, name or owner..."
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {error ? (
        <div className="empty-state"><h3>Could not load ventures</h3><p>{error.response?.data?.error || error.message}</p></div>
      ) : isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
      ) : !rows.length ? (
        <div className="empty-state" style={{ paddingTop: 40 }}>
          <h3>{data?.length ? 'No matches' : 'No ventures yet'}</h3>
          <p>{data?.length ? 'Try a different term.' : 'Add one with + New Venture.'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {rows.map(v => <VentureTile key={v.venture_id} venture={v} />)}
        </div>
      )}

      {createOpen && (
        <EventModal title="New Venture" onClose={() => setCreateOpen(false)}>
          <VentureForm
            onSubmit={createMutation.mutate}
            onCancel={() => setCreateOpen(false)}
            submitting={createMutation.isPending}
            error={createMutation.error?.response?.data?.error}
          />
        </EventModal>
      )}
    </div>
  )
}
